import type { FactionId } from "./factions.js";
import type { MissionVariant, NpcArchetype, SectorWeather } from "./contracts.js";
import type { FactionSectorId, RegionDefinition } from "./world-layout.js";
import { ALIEN_NPC_SPAWNS } from "./alien-encounters.js";

interface LocationSpec {
  id: FactionSectorId;
  name: string;
  factionId: FactionId;
  x: number;
  y: number;
  weather: SectorWeather;
  description: string;
  resourceSummary: string;
  rocks: readonly (readonly [number, number, number])[];
  npcs: readonly (readonly [NpcArchetype, number, number])[];
  huntPoint: { x: number; y: number };
  collectPoint: { x: number; y: number };
  surveyPoints: readonly { x: number; y: number }[];
  escortPath: readonly { x: number; y: number }[];
}

const LOCATION_SPECS = [
  {
    id: "concord-azure", name: "Лазурный тракт", factionId: "concord", x: 0, y: 2600, weather: "clear",
    description: "Две расходящиеся гряды прикрывают торговый маршрут. Прямой путь через развилку простреливается снайпером; конвой идёт по северному обходу.",
    resourceSummary: "Энергия у развилки, ресурсные астероиды по внешним краям и обломки налётчиков.",
    rocks: [[780, 1120, 85], [1160, 1300, 75], [1530, 1470, 90], [1780, 1050, 70], [2150, 1200, 75], [2490, 1450, 80]],
    npcs: [["raider", 1250, 750], ["raider", 2300, 800], ["sniper", 1700, 1200], ["support", 2050, 650]],
    huntPoint: { x: 1700, y: 950 }, collectPoint: { x: 1600, y: 1750 },
    surveyPoints: [{ x: 800, y: 400 }, { x: 1800, y: 500 }, { x: 2550, y: 1700 }],
    escortPath: [{ x: 360, y: 1100 }, { x: 900, y: 700 }, { x: 1900, y: 700 }, { x: 2800, y: 1200 }],
  },
  {
    id: "concord-kepler", name: "Пояс Кеплера", factionId: "concord", x: 0, y: 5200, weather: "debris-field",
    description: "Два разорванных кольца скрывают залежи. Входы находятся с разных сторон: заградителей можно обойти через внешний пояс или выбить из центра.",
    resourceSummary: "Ресурсные астероиды во внутреннем кольце, оборудование и разведчики чужих на северо-востоке.",
    rocks: [[900, 650, 100], [1300, 450, 85], [1850, 450, 85], [2300, 650, 100], [2450, 1100, 90], [2300, 1550, 100], [1850, 1750, 85], [1300, 1750, 85], [1100, 1100, 90], [1850, 900, 80], [1700, 1400, 85]],
    npcs: [["minelayer", 1450, 950], ["minelayer", 2100, 1250], ["charger", 1800, 650], ["raider", 800, 1500]],
    huntPoint: { x: 1450, y: 950 }, collectPoint: { x: 1550, y: 1200 },
    surveyPoints: [{ x: 750, y: 900 }, { x: 1900, y: 1150 }, { x: 2600, y: 1600 }], escortPath: [],
  },
  {
    id: "concord-beacons", name: "Маяки Единства", factionId: "concord", x: 0, y: 7800, weather: "clear",
    description: "Три острова камней прикрывают навигационные маяки. Между островами — открытый перелёт, где подавитель помогает снайперу удерживать дистанцию.",
    resourceSummary: "Три обследуемых маяка, энергия между островами и тайник оборудования на юге.",
    rocks: [[600, 550, 80], [950, 450, 70], [2250, 500, 85], [2600, 650, 80], [1450, 1650, 90], [1850, 1750, 75]],
    npcs: [["sniper", 1500, 850], ["disruptor", 1900, 1150], ["raider", 1050, 1400], ["raider", 2400, 1450]],
    huntPoint: { x: 1700, y: 1000 }, collectPoint: { x: 2100, y: 1750 },
    surveyPoints: [{ x: 750, y: 350 }, { x: 2400, y: 800 }, { x: 1650, y: 1900 }], escortPath: [],
  },
  {
    id: "concord-aegis", name: "Рубеж «Эгида»", factionId: "concord", x: 0, y: 10400, weather: "clear",
    description: "Каменный серп окружает позицию командира. Ремонтники держатся за его линией; разорвать поддержку можно обходом с любого конца серпа.",
    resourceSummary: "Обломки командирской группы и небольшая залежь снаружи оборонительной дуги.",
    rocks: [[1050, 500, 95], [1400, 350, 80], [1800, 350, 80], [2200, 500, 95], [2400, 850, 90], [2400, 1350, 90], [2200, 1700, 95], [1800, 1850, 80]],
    npcs: [["elite", 1700, 1050], ["support", 2050, 800], ["support", 2050, 1300], ["raider", 1350, 850], ["raider", 1350, 1300]],
    huntPoint: { x: 1700, y: 1050 }, collectPoint: { x: 750, y: 1750 },
    surveyPoints: [{ x: 850, y: 400 }, { x: 2650, y: 1100 }, { x: 1500, y: 1850 }], escortPath: [],
  },
  {
    id: "concord-harmony", name: "Верфь «Гармония»", factionId: "concord", x: 0, y: 13000, weather: "debris-field",
    description: "Домашняя база Содружества. Станция «Гармония», ресурсные астероиды и один выход к Рубежу «Эгида». Обычные противники здесь не появляются.",
    resourceSummary: "Добыча метеоритов и станция: ангар, контракты и переработка груза.",
    rocks: [[850, 500, 95], [850, 950, 90], [850, 1600, 95], [1450, 500, 85], [1550, 1100, 100], [1550, 1750, 85], [2250, 600, 95], [2250, 1250, 90], [2250, 1800, 95]],
    npcs: [],
    huntPoint: { x: 1900, y: 900 }, collectPoint: { x: 1900, y: 1550 },
    surveyPoints: [{ x: 500, y: 700 }, { x: 1200, y: 1800 }, { x: 2650, y: 500 }], escortPath: [],
  },
  {
    id: "helion-iron", name: "Железная гряда", factionId: "helion", x: 3200, y: 2600, weather: "debris-field",
    description: "Три смещённые стены оставляют чередующиеся проходы. Снайперы держат длинные коридоры, а таранщики встречают корабли на поворотах.",
    resourceSummary: "Плотная ресурсная залежь между стенами и обломки снайперов.",
    rocks: [[900, 400, 90], [900, 800, 90], [900, 1200, 90], [1600, 1000, 100], [1600, 1400, 90], [1750, 1750, 90], [2300, 400, 90], [2300, 800, 90], [2300, 1200, 90]],
    npcs: [["sniper", 1200, 650], ["sniper", 2600, 1500], ["charger", 1250, 1600], ["charger", 1950, 600]],
    huntPoint: { x: 1950, y: 900 }, collectPoint: { x: 1950, y: 1650 },
    surveyPoints: [{ x: 550, y: 1700 }, { x: 1250, y: 400 }, { x: 2650, y: 900 }], escortPath: [],
  },
  {
    id: "helion-slag", name: "Шлаковый карман", factionId: "helion", x: 3200, y: 5200, weather: "ion-storm",
    description: "Три кармана окружают открытый центр. Ресурсы лежат в разных тупиках, а выход из каждого виден кораблям на центральной площадке.",
    resourceSummary: "Энергия и ресурсные астероиды в карманах; у южной кромки замечены разведчик и подавитель чужих.",
    rocks: [[800, 500, 95], [1100, 650, 85], [800, 1400, 95], [1100, 1250, 85], [2100, 600, 90], [2450, 750, 90], [2450, 1400, 95], [2100, 1550, 85], [1400, 1750, 90], [1800, 1750, 90]],
    npcs: [["minelayer", 600, 950], ["minelayer", 2700, 1100], ["disruptor", 1650, 1000], ["support", 1800, 1350]],
    huntPoint: { x: 1650, y: 1000 }, collectPoint: { x: 2700, y: 1250 },
    surveyPoints: [{ x: 550, y: 1000 }, { x: 2650, y: 450 }, { x: 1600, y: 1950 }], escortPath: [],
  },
  {
    id: "helion-forge", name: "Дуга «Горн»", factionId: "helion", x: 3200, y: 7800, weather: "clear",
    description: "Конвой огибает расколотую промышленную дугу. Короткий путь через центр оставляет транспорт без прикрытия на северном повороте.",
    resourceSummary: "Энергия у внешних поворотов, ресурсные обломки вдоль южного края.",
    rocks: [[900, 1100, 85], [1200, 950, 90], [1600, 850, 95], [2000, 950, 90], [2300, 1100, 85], [1250, 1550, 75], [1950, 1550, 75]],
    npcs: [["minelayer", 1200, 450], ["disruptor", 2250, 600], ["raider", 650, 750], ["raider", 2650, 1450]],
    huntPoint: { x: 1700, y: 500 }, collectPoint: { x: 1600, y: 1850 },
    surveyPoints: [{ x: 700, y: 1700 }, { x: 1600, y: 450 }, { x: 2500, y: 1750 }],
    escortPath: [{ x: 450, y: 1500 }, { x: 650, y: 650 }, { x: 1500, y: 400 }, { x: 2450, y: 650 }, { x: 2800, y: 1550 }],
  },
  {
    id: "helion-imperative", name: "Узел «Императив»", factionId: "helion", x: 3200, y: 10400, weather: "clear",
    description: "Четыре опорных укрытия контролируют центральный узел. Командир связан снайпером, подавителем и ремонтником: выбор первой цели меняет весь бой.",
    resourceSummary: "Обломки специалистов и командирский контейнер; ресурсные точки вынесены на фланги.",
    rocks: [[1200, 700, 115], [2000, 700, 115], [1200, 1500, 115], [2000, 1500, 115], [700, 1100, 70], [2500, 1100, 70]],
    npcs: [["elite", 1600, 1100], ["sniper", 1600, 450], ["disruptor", 2250, 1100], ["support", 1600, 1750]],
    huntPoint: { x: 1600, y: 1100 }, collectPoint: { x: 2650, y: 1750 },
    surveyPoints: [{ x: 650, y: 500 }, { x: 2550, y: 500 }, { x: 1600, y: 1950 }], escortPath: [],
  },
  {
    id: "helion-rift", name: "Разлом Гелиона", factionId: "helion", x: 3200, y: 13000, weather: "ion-storm",
    description: "Домашняя база Гелиона с ресурсными астероидами. Единственные врата ведут к Узлу «Императив»; обычных противников на базе нет.",
    resourceSummary: "Метеориты и база Гелиона: ангар, контракты и переработка груза.",
    rocks: [[900, 650, 120], [900, 1100, 100], [900, 1550, 120], [2300, 650, 120], [2300, 1100, 100], [2300, 1550, 120]],
    npcs: [],
    huntPoint: { x: 1650, y: 1000 }, collectPoint: { x: 1600, y: 1850 },
    surveyPoints: [{ x: 550, y: 650 }, { x: 1600, y: 400 }, { x: 2650, y: 1600 }], escortPath: [],
  },
  {
    id: "nomads-magpie", name: "Тропа Сороки", factionId: "nomads", x: 6400, y: 2600, weather: "clear",
    description: "S-образный пояс ведёт транспорт через три разворота. Прямые срезы открыты для налётчиков и не заменяют полёт рядом с конвоем.",
    resourceSummary: "Энергия на открытых срезах, обломки налётчиков и небольшие ресурсные скопления у поворотов.",
    rocks: [[900, 700, 90], [1250, 650, 80], [1500, 950, 90], [1700, 1300, 90], [2000, 1550, 80], [2400, 1500, 90]],
    npcs: [["charger", 1700, 750], ["raider", 1100, 1300], ["sniper", 2450, 950], ["raider", 2100, 1850]],
    huntPoint: { x: 1700, y: 750 }, collectPoint: { x: 1550, y: 1800 },
    surveyPoints: [{ x: 600, y: 1700 }, { x: 1700, y: 450 }, { x: 2700, y: 1650 }],
    escortPath: [{ x: 400, y: 1050 }, { x: 800, y: 1500 }, { x: 1300, y: 1650 }, { x: 1850, y: 650 }, { x: 2400, y: 500 }, { x: 2800, y: 1050 }],
  },
  {
    id: "nomads-graveyard", name: "Могильник «Кочевник»", factionId: "nomads", x: 6400, y: 5200, weather: "debris-field",
    description: "Четыре тупика среди остовов скрывают оборудование. Внешний обход соединяет их выходы, а заградители мешают возвращаться прежним путём.",
    resourceSummary: "Тайники, три скан-маяка и ресурсные остовы; на северо-востоке охотятся стрелок и подавитель чужих.",
    rocks: [[800, 500, 90], [1200, 500, 90], [1200, 900, 90], [2000, 500, 90], [2400, 500, 90], [2000, 900, 90], [800, 1600, 90], [1200, 1600, 90], [1200, 1250, 90], [2000, 1600, 90], [2400, 1600, 90], [2000, 1250, 90]],
    npcs: [["minelayer", 950, 1100], ["minelayer", 2350, 1100], ["charger", 1600, 750], ["disruptor", 1600, 1450]],
    huntPoint: { x: 1600, y: 1100 }, collectPoint: { x: 2550, y: 1800 },
    surveyPoints: [{ x: 1000, y: 700 }, { x: 2250, y: 700 }, { x: 2250, y: 1400 }], escortPath: [],
  },
  {
    id: "nomads-mist", name: "Туманная петля", factionId: "nomads", x: 6400, y: 7800, weather: "ion-storm",
    description: "Ионная буря ослабляет щиты. Длинная внешняя петля прикрыта камнями, короткий перелёт к энергии проходит через позиции подавителей.",
    resourceSummary: "Энергия в центре петли, ресурсные астероиды на внешней дуге и обломки подавителей.",
    rocks: [[900, 500, 85], [1350, 350, 85], [1900, 350, 85], [2350, 550, 85], [2500, 1400, 90], [2050, 1750, 90], [1350, 1800, 85], [950, 1650, 90], [1250, 1100, 85], [2000, 1100, 85]],
    npcs: [["disruptor", 1550, 800], ["disruptor", 1800, 1400], ["sniper", 2300, 1000], ["support", 1550, 1450]],
    huntPoint: { x: 1550, y: 800 }, collectPoint: { x: 1650, y: 1100 },
    surveyPoints: [{ x: 650, y: 750 }, { x: 1600, y: 550 }, { x: 2600, y: 1750 }], escortPath: [],
  },
  {
    id: "nomads-shards", name: "Осколки Свободы", factionId: "nomads", x: 6400, y: 10400, weather: "debris-field",
    description: "Четыре острова обломков окружают пустой центр. Быстрый вывоз через середину заметен всем, путь по периметру длиннее, но даёт укрытия.",
    resourceSummary: "Оборудование на трёх островах, ресурсные астероиды на четвёртом и обломки таранщиков.",
    rocks: [[700, 500, 90], [1050, 650, 75], [2200, 500, 90], [2550, 650, 75], [700, 1550, 90], [1050, 1750, 75], [2200, 1550, 90], [2550, 1750, 75]],
    npcs: [["charger", 1300, 1000], ["charger", 2000, 1250], ["support", 1800, 1500], ["sniper", 2450, 950]],
    huntPoint: { x: 1600, y: 1100 }, collectPoint: { x: 2600, y: 1400 },
    surveyPoints: [{ x: 800, y: 800 }, { x: 2350, y: 800 }, { x: 800, y: 1900 }], escortPath: [],
  },
  {
    id: "nomads-last-chance", name: "Рубеж «Последний шанс»", factionId: "nomads", x: 6400, y: 13000, weather: "clear",
    description: "Домашняя база Вольных систем. Станция «Последний шанс», метеориты и один выход к Осколкам Свободы. Обычных противников нет.",
    resourceSummary: "Ресурсные астероиды и станция: ангар, контракты и переработка груза.",
    rocks: [[1100, 600, 90], [1600, 450, 95], [2100, 600, 90], [1100, 1600, 90], [1600, 1800, 95], [2100, 1600, 90], [2450, 1100, 80]],
    npcs: [],
    huntPoint: { x: 1900, y: 1050 }, collectPoint: { x: 2700, y: 1500 },
    surveyPoints: [{ x: 800, y: 600 }, { x: 1600, y: 1100 }, { x: 2700, y: 1750 }], escortPath: [],
  },
] satisfies readonly LocationSpec[];

export interface FactionLocationDefinition extends RegionDefinition {
  id: FactionSectorId;
  factionId: FactionId;
  huntPoint: { x: number; y: number };
  collectPoint: { x: number; y: number };
  surveyPoints: readonly { x: number; y: number }[];
  escortPath: readonly { x: number; y: number }[];
}
export const FACTION_LOCATIONS: readonly FactionLocationDefinition[] = LOCATION_SPECS.map((spec) => {
  const point = (value: { x: number; y: number }) => ({ x: spec.x + value.x, y: spec.y + value.y });
  const variant: MissionVariant = spec.weather === "ion-storm" ? "ambush" : spec.weather === "debris-field" ? "reinforced" : "standard";
  return {
    id: spec.id, name: spec.name, factionId: spec.factionId, x: spec.x, y: spec.y, width: 3200, height: 2200,
    weather: spec.weather, variant, pvpAllowed: true, description: spec.description, resourceSummary: spec.resourceSummary,
    huntPoint: point(spec.huntPoint), collectPoint: point(spec.collectPoint), surveyPoints: spec.surveyPoints.map(point), escortPath: spec.escortPath.map(point),
  };
});
export const FACTION_OBSTACLES = LOCATION_SPECS.map((spec) => ({
  sectorId: spec.id,
  obstacles: spec.rocks.map(([x, y, radius], index) => ({ id: `${spec.id}-rock-${index + 1}`, x: spec.x + x, y: spec.y + y, radius })),
}));
export interface WorldNpcSpawn {
  id: string;
  sectorId: FactionSectorId;
  archetype: NpcArchetype;
  x: number;
  y: number;
}
export const WORLD_NPC_SPAWNS: readonly WorldNpcSpawn[] = [...LOCATION_SPECS.flatMap((spec) =>
  spec.npcs.map(([archetype, x, y], index) => ({ id: `${spec.id}-${archetype}-${index + 1}`, sectorId: spec.id, archetype, x: spec.x + x, y: spec.y + y })),
), ...ALIEN_NPC_SPAWNS];
